/**
 * Value resolution for the React event detail template.
 *
 * The page this replaces was authored in the Page Builder HTML/CSS workspace,
 * where every value arrived through `{{ event.start_datetime | datetime }}`.
 * Those placeholders are gone, but their SEMANTICS must not be: a datetime is
 * still read in the event's own timezone, a currency still renders to two
 * decimals, and an unresolvable reference still degrades to an empty string
 * rather than printing "undefined" at a visitor.
 *
 * So the same helpers the renderer uses are reused here rather than a second
 * set of formatting rules being written alongside them. If `applyFilter` gains
 * a case or changes a format, this page moves with it.
 */

import { resolvePath, applyFilter, timeZoneForPath } from '../../../utils/recordTemplate';
import { isSafeUrl } from '../../../utils/templateSanitiser';

/**
 * Truthiness for the conditions the template expressed as `data-arcee-if`.
 *
 * An empty array is FALSE — "show this block if there are any categories" is
 * the intent, and JS would otherwise call [] truthy. Kept identical to
 * renderRecordTemplate's own isTruthy so a section that appeared under the
 * Page Builder appears here, and one that did not, does not.
 */
export function isTruthy(value) {
  if (Array.isArray(value)) return value.length > 0;
  if (value === null || value === undefined || value === '') return false;
  return Boolean(value);
}

/**
 * Builds the value reader for one scope.
 *
 * `recordAlias` names the record the page is drawing, so a related block's
 * datetime (`registration.registration_end_datetime`) can fall back to the
 * timezone the event declares — the related row has none of its own.
 *
 *   const fmt = createFormatter(scope, 'event');
 *   fmt('event.start_datetime', 'datetime')   // → "12 Sep 2026 06:30"
 *   fmt('registration.fee_amount', 'currency')
 *   fmt('event.title')
 */
export function createFormatter(scope, recordAlias = 'event') {
  return function fmt(path, filter, arg) {
    let value = resolvePath(scope, path);

    if (filter) {
      value = applyFilter(value, filter, arg, timeZoneForPath(scope, path, recordAlias));
    }

    if (value === null || value === undefined) return '';
    return String(value);
  };
}

/**
 * Formats a value already in hand — a row inside a repeat, where there is no
 * path to resolve. The filter rules are the shared ones, so `| number` means
 * here exactly what it meant in the template.
 */
export function formatValue(value, filter, arg, timeZone) {
  const formatted = filter ? applyFilter(value, filter, arg, timeZone) : value;
  if (formatted === null || formatted === undefined) return '';
  return String(formatted);
}

/** Reads a raw value, unformatted — for conditions rather than display. */
export function readValue(scope, path) {
  return resolvePath(scope, path);
}

/**
 * A URL from record data, or null.
 *
 * The template dropped an unsafe `href`/`src` rather than rendering it, which
 * is what kept a `javascript:` value typed into an admin field from becoming a
 * live link on a public page. A React `href` is no safer, so the same check
 * guards every URL that reaches this page from the record.
 */
export function safeUrl(value) {
  if (value === null || value === undefined || value === '') return null;
  const url = String(value);
  return isSafeUrl(url) ? url : null;
}

/**
 * The event's name, as this page shows it.
 *
 * The heading reads the event's own `title` column (the template renders
 * `event.title`). The breadcrumb, the browser-tab title and the registration
 * modal used to read the view's Title mapping instead — and a default mapping
 * can name the event's first plain-text column, so the trail read
 * "ORGANIZATION" under a heading that read "Ulsoor Lake 10K". Reading the same
 * column the heading reads keeps all of them in agreement.
 *
 * The mapped column remains the fallback, for an event saved without a title.
 */
export function eventTitleOf(record, fieldMapping) {
  const own = record?.title;
  if (typeof own === 'string' && own.trim() !== '') return own;
  return record?.[fieldMapping?.title_field];
}
